import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Settings → Tab "Multi-Provider-Test"
 *
 * Schickt denselben Prompt parallel an 1-5 LLM-Konfigurationen, jede optional
 * mit einer Review-Rolle. Antwort kommt nach max. 60s zurück, alle Spalten
 * werden nebeneinander dargestellt. Einzelfehler bleiben isoliert (rote Card).
 */
export const MultiProviderTest = () => {
  const [configs, setConfigs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selected, setSelected] = useState({}); // { config_id: { role_id|"" } }
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [run, setRun] = useState(null); // { parallel_run_id, results }

  useEffect(() => {
    Promise.all([
      api.get("/llm-configs").catch(() => ({ data: [] })),
      api.get("/llm/review-roles").catch(() => ({ data: [] })),
    ]).then(([c, r]) => {
      setConfigs(c.data);
      setRoles(r.data);
    });
  }, []);

  const toggleConfig = (cfgId) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[cfgId]) {
        delete next[cfgId];
      } else if (Object.keys(next).length < 5) {
        next[cfgId] = { role_id: "" };
      } else {
        toast.error("Maximal 5 parallele Aufrufe.");
      }
      return next;
    });
  };

  const setRole = (cfgId, roleId) => {
    setSelected((prev) => ({ ...prev, [cfgId]: { role_id: roleId } }));
  };

  const supported = configs.filter((c) => ["openai", "anthropic", "google"].includes(c.provider));

  const submit = async () => {
    const calls = Object.entries(selected).map(([config_id, v]) => ({
      config_id,
      role_id: v.role_id || null,
    }));
    if (calls.length < 1) {
      toast.error("Mindestens eine Konfiguration auswählen.");
      return;
    }
    if (!prompt.trim()) {
      toast.error("Prompt ist Pflicht.");
      return;
    }
    setBusy(true);
    setRun(null);
    try {
      const r = await api.post("/llm/generate-parallel", {
        prompt: prompt.trim(),
        calls,
      });
      setRun(r.data);
      toast.success(`Parallel-Run abgeschlossen (${r.data.results.length} Antworten).`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Parallel-Run fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="multi-provider-tab" className="space-y-6">
      <div className="text-sm text-neutral-600">
        Schickt denselben Prompt parallel an bis zu 5 LLM-Konfigurationen. Pro Aufruf optional eine Review-Rolle.
      </div>

      {supported.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900" data-testid="multi-no-configs">
          Keine unterstützten LLM-Konfigurationen vorhanden. Bitte unter „LLM-Konfiguration" erst eine OpenAI-, Anthropic- oder Google-Config anlegen.
        </div>
      ) : (
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">
            Konfigurationen ({Object.keys(selected).length}/5)
          </div>
          <div className="border border-neutral-200 divide-y divide-neutral-100">
            {supported.map((c) => {
              const sel = !!selected[c.config_id];
              return (
                <div key={c.config_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`multi-cfg-${c.config_id}`}>
                  <input
                    type="checkbox"
                    checked={sel}
                    onChange={() => toggleConfig(c.config_id)}
                    data-testid={`multi-cfg-check-${c.config_id}`}
                  />
                  <div className="flex-1">
                    <div className="text-sm text-neutral-900 font-medium">{c.name}</div>
                    <div className="font-mono text-[10px] text-neutral-500 uppercase tracking-widest">
                      {c.provider} · {c.model}
                    </div>
                  </div>
                  {sel && (
                    <select
                      data-testid={`multi-role-${c.config_id}`}
                      value={selected[c.config_id]?.role_id || ""}
                      onChange={(e) => setRole(c.config_id, e.target.value)}
                      className="text-xs border border-neutral-300 px-2 py-1.5"
                    >
                      <option value="">— ohne Rolle —</option>
                      {roles.map((r) => (
                        <option key={r.role_id} value={r.role_id}>{r.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Prompt</label>
        <textarea
          data-testid="multi-prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={5}
          placeholder="Welche zwei größten Risiken siehst du bei diesem Vorhaben?"
          className="w-full border border-neutral-300 px-3 py-2 text-sm"
        />
      </div>

      <div className="flex justify-end">
        <button
          data-testid="multi-submit"
          disabled={busy || Object.keys(selected).length === 0}
          onClick={submit}
          className="text-xs font-mono uppercase tracking-widest text-white bg-neutral-900 hover:bg-neutral-700 disabled:bg-neutral-400 px-4 py-2"
        >
          {busy ? "Läuft … (max. 60s)" : "Parallel ausführen"}
        </button>
      </div>

      {run && (
        <div data-testid="multi-results" className="space-y-2">
          <div className="font-mono text-[10px] text-neutral-400 uppercase tracking-widest">
            Run-ID: {run.parallel_run_id}
          </div>
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${run.results.length}, minmax(0, 1fr))` }}>
            {run.results.map((r, idx) => (
              <ResultCard key={idx} result={r} roles={roles} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ResultCard = ({ result, roles }) => {
  const isError = result.status === "error";
  const role = roles.find((r) => r.role_id === result.role_id);
  return (
    <div
      data-testid={`multi-result-${result.config_id}`}
      className={`border ${isError ? "border-rose-300 bg-rose-50" : "border-neutral-200 bg-white"}`}
    >
      <div className={`px-3 py-2 border-b ${isError ? "border-rose-200" : "border-neutral-100"}`}>
        <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-700">
          {result.provider || "?"} · {result.model || "?"}
        </div>
        <div className="text-[10px] text-neutral-500 mt-0.5">
          {role ? `Rolle: ${role.name}` : "ohne Rolle"}
          {result.latency_ms != null && <> · {result.latency_ms} ms</>}
          {result.tokens && <> · {result.tokens.in}→{result.tokens.out} tok</>}
        </div>
      </div>
      <div className="px-3 py-3 text-sm">
        {isError ? (
          <div className="text-rose-900">
            <div className="font-mono text-[10px] uppercase tracking-widest text-rose-800 mb-1">Fehler</div>
            {result.error}
          </div>
        ) : (
          <div className="whitespace-pre-wrap text-neutral-900">{result.content}</div>
        )}
      </div>
    </div>
  );
};
